import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  Settings, Plus, Edit, Trash2, FileText, Upload, X, Eye, Download, 
  Loader2, File, Image, FileSpreadsheet, AlertCircle, Users, FileSignature
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('fortis_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const BranchSettings = ({ branchIdOverride, readOnly = false }) => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [workPanelUsers, setWorkPanelUsers] = useState([]); // İş Paneli kullanıcıları
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    files: [],
    visible_to_users: [], // Bu kartı görebilecek kullanıcılar
    contract_viewers: [] // Sözleşme tabını görebilecek kullanıcılar
  });
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadCards();
    loadWorkPanelUsers();
  }, []);

  const loadWorkPanelUsers = async () => {
    try {
      const res = await axios.get(`${API}/work/panel-users`, { headers: getAuthHeaders() });
      setWorkPanelUsers(res.data || []);
    } catch (error) {
      console.error('İş paneli kullanıcıları yüklenirken hata:', error);
    }
  };

  const loadCards = async () => {
    try {
      const res = await axios.get(`${API}/branch/info-cards`, { headers: getAuthHeaders() });
      setCards(res.data || []);
    } catch (error) {
      console.error('Kartlar yüklenirken hata:', error);
      toast.error('Kartlar yüklenemedi');
    }
    setLoading(false);
  };

  const openAddModal = () => {
    setEditingCard(null);
    setFormData({ title: '', description: '', files: [], visible_to_users: [], contract_viewers: [] });
    setModalOpen(true);
  };

  const openEditModal = (card) => {
    setEditingCard(card);
    setFormData({
      title: card.title,
      description: card.description,
      files: card.files || [],
      visible_to_users: card.visible_to_users || [],
      contract_viewers: card.contract_viewers || []
    });
    setModalOpen(true);
  };

  const toggleUserVisibility = (userId) => {
    setFormData(prev => ({
      ...prev,
      visible_to_users: prev.visible_to_users.includes(userId)
        ? prev.visible_to_users.filter(id => id !== userId)
        : [...prev.visible_to_users, userId]
    }));
  };

  const toggleContractViewer = (userId) => {
    setFormData(prev => ({
      ...prev,
      contract_viewers: prev.contract_viewers.includes(userId)
        ? prev.contract_viewers.filter(id => id !== userId)
        : [...prev.contract_viewers, userId]
    }));
  };

  const selectAllUsers = () => {
    setFormData(prev => ({
      ...prev,
      visible_to_users: workPanelUsers.map(u => u.id)
    }));
  };

  const deselectAllUsers = () => {
    setFormData(prev => ({
      ...prev,
      visible_to_users: []
    }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Dosya boyutu 10MB\'dan küçük olmalıdır');
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const res = await axios.post(`${API}/branch/info-cards/upload`, formDataUpload, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });

      setFormData(prev => ({
        ...prev,
        files: [...prev.files, res.data]
      }));
      toast.success('Dosya yüklendi');
    } catch (error) {
      console.error('Dosya yüklenirken hata:', error);
      toast.error('Dosya yüklenemedi');
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index) => {
    setFormData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error('Başlık zorunludur');
      return;
    }

    setSubmitting(true);
    try {
      if (editingCard) {
        await axios.put(`${API}/branch/info-cards/${editingCard.id}`, formData, { headers: getAuthHeaders() });
        toast.success('Kart güncellendi');
      } else {
        await axios.post(`${API}/branch/info-cards`, formData, { headers: getAuthHeaders() });
        toast.success('Kart oluşturuldu');
      }
      setModalOpen(false);
      loadCards();
    } catch (error) {
      console.error('Kaydetme hatası:', error);
      toast.error('İşlem başarısız');
    }
    setSubmitting(false);
  };

  const handleDelete = async (cardId) => {
    if (!window.confirm('Bu kartı silmek istediğinizden emin misiniz?')) return;

    try {
      await axios.delete(`${API}/branch/info-cards/${cardId}`, { headers: getAuthHeaders() });
      toast.success('Kart silindi');
      loadCards();
    } catch (error) {
      console.error('Silme hatası:', error);
      toast.error('Kart silinemedi');
    }
  };

  const getFileIcon = (type) => {
    if (type?.includes('image')) return <Image className="h-5 w-5 text-green-600" />;
    if (type?.includes('pdf')) return <FileText className="h-5 w-5 text-red-600" />;
    if (type?.includes('spreadsheet') || type?.includes('excel')) return <FileSpreadsheet className="h-5 w-5 text-green-600" />;
    return <File className="h-5 w-5 text-blue-600" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleDownload = (file) => {
    const link = document.createElement('a');
    link.href = file.url;
    link.download = file.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="branch-settings">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Şube Ayarları</h1>
          <p className="text-gray-500">Şube bilgi kartlarını yönetin - Çalışanlar bu kartları İş Paneli'nde görebilir</p>
        </div>
        <Button onClick={openAddModal} className="bg-amber-600 hover:bg-amber-700" data-testid="add-card-btn">
          <Plus className="h-4 w-4 mr-2" />
          Yeni Kart Ekle
        </Button>
      </div>

      {/* Info Cards Grid */}
      {cards.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Settings className="h-16 w-16 mx-auto mb-4 text-gray-200" />
            <h3 className="text-xl font-bold mb-2">Henüz Bilgi Kartı Yok</h3>
            <p className="text-gray-500 mb-4">
              "Yeni Kart Ekle" butonuna tıklayarak şube bilgi kartları oluşturabilirsiniz.
              Bu kartlar çalışanların İş Paneli'nde görünecektir.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {cards.map(card => (
            <Card key={card.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg">{card.title}</CardTitle>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEditModal(card)} data-testid={`edit-card-${card.id}`}>
                      <Edit className="h-4 w-4 text-blue-600" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(card.id)} data-testid={`delete-card-${card.id}`}>
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm mb-3 line-clamp-3">{card.description || 'Açıklama yok'}</p>
                
                {/* Files */}
                {card.files && card.files.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase">Ekli Dosyalar ({card.files.length})</p>
                    {card.files.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                        <div className="flex items-center gap-2 truncate">
                          {getFileIcon(file.type)}
                          <span className="truncate">{file.filename}</span>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPreviewFile(file)}>
                            <Eye className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDownload(file)}>
                            <Download className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <p className="text-xs text-gray-400 mt-3">
                  {new Date(card.created_at).toLocaleDateString('tr-TR')}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-amber-600" />
              {editingCard ? 'Kartı Düzenle' : 'Yeni Kart Ekle'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Başlık *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Kart başlığı"
                data-testid="card-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Açıklama</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Kart açıklaması..."
                rows={4}
                data-testid="card-description-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Dosya Ekle</Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif"
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Yükleniyor...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Dosya Seç
                    </>
                  )}
                </Button>
                <p className="text-xs text-gray-500 mt-2">PDF, Word, Excel, PNG, JPG (max 10MB)</p>
              </div>

              {/* Uploaded Files List */}
              {formData.files.length > 0 && (
                <div className="space-y-2 mt-3">
                  {formData.files.map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center gap-2 truncate">
                        {getFileIcon(file.type)}
                        <span className="text-sm truncate">{file.filename}</span>
                        <span className="text-xs text-gray-400">({formatFileSize(file.size)})</span>
                      </div>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeFile(idx)}>
                        <X className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Görünürlük Ayarları */}
            <div className="space-y-2 border-t pt-4">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-blue-600" />
                  Bu Kartı Görebilecek İş Paneli Kullanıcıları
                </Label>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={selectAllUsers}>
                    Tümünü Seç
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={deselectAllUsers}>
                    Temizle
                  </Button>
                </div>
              </div>
              <p className="text-xs text-gray-500 mb-2">Seçilmezse hiç kimse bu kartı göremez</p>
              <ScrollArea className="h-32 border rounded-lg p-2">
                {workPanelUsers.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">İş paneli kullanıcısı bulunamadı</p>
                ) : (
                  <div className="space-y-2">
                    {workPanelUsers.map(user => (
                      <div key={user.id} className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded">
                        <Checkbox
                          id={`visible-${user.id}`}
                          checked={formData.visible_to_users.includes(user.id)}
                          onCheckedChange={() => toggleUserVisibility(user.id)}
                        />
                        <label htmlFor={`visible-${user.id}`} className="text-sm cursor-pointer flex-1">
                          {user.name || user.username}
                          {user.role && <span className="text-xs text-gray-400 ml-2">({user.role})</span>}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
              {formData.visible_to_users.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {formData.visible_to_users.map(userId => {
                    const user = workPanelUsers.find(u => u.id === userId);
                    return user ? (
                      <Badge key={userId} variant="secondary" className="text-xs">
                        {user.name || user.username}
                      </Badge>
                    ) : null;
                  })}
                </div>
              )}
            </div>

            {/* Sözleşme Görüntüleme İzni */}
            <div className="space-y-2 border-t pt-4">
              <Label className="flex items-center gap-2">
                <FileSignature className="h-4 w-4 text-amber-600" />
                Sözleşme Tabını Görebilecek Kullanıcılar
              </Label>
              <p className="text-xs text-gray-500 mb-2">Bu kullanıcılar taşeronun sözleşme bilgilerini görebilir</p>
              <ScrollArea className="h-32 border rounded-lg p-2">
                {workPanelUsers.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">İş paneli kullanıcısı bulunamadı</p>
                ) : (
                  <div className="space-y-2">
                    {workPanelUsers.map(user => (
                      <div key={user.id} className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded">
                        <Checkbox
                          id={`contract-${user.id}`}
                          checked={formData.contract_viewers.includes(user.id)}
                          onCheckedChange={() => toggleContractViewer(user.id)}
                        />
                        <label htmlFor={`contract-${user.id}`} className="text-sm cursor-pointer flex-1">
                          {user.name || user.username}
                          {user.role && <span className="text-xs text-gray-400 ml-2">({user.role})</span>}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
              {formData.contract_viewers.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {formData.contract_viewers.map(userId => {
                    const user = workPanelUsers.find(u => u.id === userId);
                    return user ? (
                      <Badge key={userId} variant="outline" className="text-xs border-amber-500 text-amber-700">
                        {user.name || user.username}
                      </Badge>
                    ) : null;
                  })}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>İptal</Button>
            <Button onClick={handleSubmit} disabled={submitting} className="bg-amber-600 hover:bg-amber-700">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingCard ? 'Güncelle' : 'Oluştur'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* File Preview Modal */}
      <Dialog open={!!previewFile} onOpenChange={() => setPreviewFile(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{previewFile?.filename}</span>
              <Button variant="outline" size="sm" onClick={() => handleDownload(previewFile)}>
                <Download className="h-4 w-4 mr-2" />
                İndir
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 overflow-auto max-h-[70vh]">
            {previewFile?.type?.includes('image') ? (
              <img src={previewFile.url} alt={previewFile.filename} className="max-w-full mx-auto" />
            ) : previewFile?.type?.includes('pdf') ? (
              <iframe src={previewFile.url} className="w-full h-[60vh]" title={previewFile.filename} />
            ) : (
              <div className="text-center py-12">
                <AlertCircle className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">Bu dosya türü önizlenemez.</p>
                <Button variant="outline" className="mt-4" onClick={() => handleDownload(previewFile)}>
                  <Download className="h-4 w-4 mr-2" />
                  Dosyayı İndir
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BranchSettings;
